import { useAuthState } from '@packages/contexts/auth';
import { useContentSettings } from '@packages/contexts/content-settings/content-settings.hooks';
import { fetchTechline, useTechlineDispatch } from '@packages/contexts/techline';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { type RetailerSearchResult } from '@packages/models/api';
import { AlertBar, Button, Icon, Typography } from '@packages/ui/shared';
import RecentSearchTypeahead from '@web/components/recent-search-typeahead';
import { TablePagination, TableRenderer } from '@web/components/table';
import { useContentOverlayState, usePermission, useQuery } from '@web/core/hooks';
import { analyticsService, httpClient, qmrsService, techlineService } from '@web/services/singletons';
import { caseStatusMap as statusMap } from '@web/utils';
import { useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import { useMatch, useNavigate } from 'react-router-dom';

import { CreateCaseModalV2 } from '../components/create-case-modal/v2/create-case-modal-v2';
import { SearchRetailer } from '../components/search-retailer';
import { EmptyStateDescription, TechlineTableHeader, TechlineTableRow } from './components/case-table';
import { DataCard } from './components/data-card';
import { FiltersComponent } from './components/filters';
import { TechlineQuickFilters } from './components/quick-filters';
import { StatusTabs } from './components/status-tab';

const logger = getLogger('techline');

const ALLOW_VIEW_DRAFT_PERMISSION = 'TECHLINE015';

const useStyles = createUseStyles({
    header: {
        padding: '24px',
    },
    container: {
        padding: '0 24px',
    },
    filtersContainer: {
        padding: '16px 24px',
    },
    filtersLeftContainer: {
        gap: '16px',
        flexDirection: 'row',

        '@media(max-width: 940px)': {
            flexDirection: 'column !important',
            alignItems: 'start !important',
        },
    },
    techlineSearch: {
        '@media(min-width: 1200px)': {
            width: '340px',
        },
    },
    outerTable: {
        height: '100%',
        minHeight: 'auto',
    },

    outerTableEmpty: {
        height: '50vh',
        minHeight: 'auto',
    },
    headerSearchRetailer: {
        minWidth: 256,
    },
});

const styles = StyleSheet.create({
    headerTitle: {
        fontSize: 27,
        fontWeight: '400',
        lineHeight: 40,
    },
});

const emptyFilters = {
    vin: '',
    selectedStatus: [],
    selectedWorksheets: [],
    selectedSubmissionDate: [],
};

export function DashboardView() {
    const classes = useStyles();
    const navigate = useNavigate();
    const queryParams = useQuery();
    const showCloseCaseSuccessPopupForCaseNumber = queryParams.get('showCloseCaseSuccessFor');
    const showSubmitCaseSuccessForCaseNumber = queryParams.get('showSubmitCaseSuccessFor');
    const retailerNumber = queryParams.get('retailerNumber');

    const { account } = useAuthState();
    const { abortSignalRef, signalAbort } = useAbortController();
    const techlineDispatch = useTechlineDispatch();
    const [showCreateCaseModal, setShowCreateCaseModal] = useState(false);
    const [selectedRetailer, setSelectedRetailer] = useState<RetailerSearchResult[]>([]);
    const [allRetailers, setAllRetailers] = useState<RetailerSearchResult[] | any[]>([]);
    const [loadingCaseList, setLoadingCaseList] = useState(false);
    const [allCases, setAllCases] = useState<any>([]);
    const [showCloseCaseSuccessPopup, setShowCloseCaseSuccessPopup] = useState(false);
    const [permissions, setPermissions] = useState<any>([]);
    const { state: dashboardState, loadDashboards } = useContentSettings();
    const techlineContents = dashboardState.dashboardData?.techline || [];
    const videoCardData = techlineContents.filter((content) => content.contentType === 'VIDEO')?.[0] || {};
    const quickTipCardData = techlineContents.filter((content) => content.contentType === 'QUICK_TIP')?.[0] || {};

    const [pageSize, setPageSize] = useState(10);
    const [pageIndex, setPageIndex] = useState(0);
    const [totalCases, setTotalCases] = useState(0);

    const [filters, setFilters] = useState<any>(emptyFilters);
    const [currentStatusFilter, setCurrentStatusFilter] = useState('ALL');

    const { toggledOverlays, toggleOverlay } = useContentOverlayState();
    const [submitSuccessCaseNumber, setSubmitSuccessCaseNumber] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [displayQueryError, setDisplayQueryError] = useState(false);
    const [isNonRetailer, setIsNonRetailer] = useState(false);
    const searchInputRef = useRef(null);
    const [showCloseCaseSuccessPopupFlag, setShowCloseCaseSuccessPopupFlag] = useState<boolean>(false);
    const { checkPermission } = usePermission(permissions);

    const vinTechlineCaseDashboardRouteMatch = useMatch('/vin/:id/techline');
    const { selectedVin } = useVinSessionState();

    function handleEditContent() {
        navigate('/administration/content-settings/techline');
    }

    useEffect(() => {
        if (showSubmitCaseSuccessForCaseNumber) {
            // Store the case number in state so the message survives URL changes
            setSubmitSuccessCaseNumber(showSubmitCaseSuccessForCaseNumber);

            // Strip the query param immediately so it doesn't re-trigger on navigation
            navigate('/techline/dashboard', { replace: true });

            // Auto-dismiss after 10 seconds
            setTimeout(() => {
                setSubmitSuccessCaseNumber(null);
            }, 10000);
        }
    }, [navigate, showSubmitCaseSuccessForCaseNumber]);

    useEffect(() => {
        fetchTechline({
            techlineId: undefined,
            techlineDispatch,
            techlineService,
        });
    }, [techlineDispatch]);

    useEffect(() => {
        if (showCloseCaseSuccessPopupForCaseNumber) {
            setShowCloseCaseSuccessPopup(true);
            setShowCloseCaseSuccessPopupFlag(true);
            setTimeout(() => {
                navigate('/techline/dashboard', { replace: true });
                setShowCloseCaseSuccessPopup(false);
                setShowCloseCaseSuccessPopupFlag(false);
            }, 3000);
        }
    }, [showCloseCaseSuccessPopupForCaseNumber]);

    useEffect(() => {
        if (!showCloseCaseSuccessPopupForCaseNumber) {
            const fetchRetailers = async () => {
                try {
                    const retailersRes = await techlineService.retrieveRetailer(false, abortSignalRef.current);
                    if (retailersRes?.data?.results?.length) {
                        const retailers = retailersRes.data.results;

                        setAllRetailers(retailers);

                        if (retailerNumber) {
                            const selectedRetailer = retailers.find(
                                (d: { retailerNumber: string }) => d.retailerNumber === retailerNumber
                            );

                            if (selectedRetailer) {
                                setSelectedRetailer([selectedRetailer]);
                                navigate('/techline/dashboard', { replace: true });
                                return;
                            }
                        }

                        setSelectedRetailer([retailers[0]]);
                    } else {
                        setIsNonRetailer(true);
                        fetchSearchHistory();
                    }
                } catch (error) {
                    logger.error('Failed to fetch retailers', { error });
                    alert('An error occurred while retrieving retailer data. Please refresh and try again.');
                }
            };

            fetchRetailers();

            return () => {
                if (abortSignalRef.current) {
                    signalAbort();
                }
            };
        }
    }, [showCloseCaseSuccessPopupForCaseNumber]);

    const fetchSearchHistory = async () => {
        try {
            const response = await techlineService.recentSearch(false, abortSignalRef.current);
            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                throw response.data;
            }
            if (response.data.retailerSearch.length) {
                setAllRetailers(
                    response.data.retailerSearch.map((d: { name: any; code: any }) => ({
                        retailerName: d.name,
                        retailerNumber: d.code,
                    }))
                );
            }
        } catch (err) {
            logger.error('Failed to fetch recent retailer search history', { error: err });
        }
    };

    const fetchPermissions = useCallback(() => {
        if (!account || !account.accountId) {
            return;
        }
        const config = {
            method: 'GET' as const,
            signal: abortSignalRef.current,
            ignoreCache: true,
        };
        httpClient
            .orchestrateRequest<any>({
                ...config,
                url: `/accounts/${account.accountId}/permissions-debug`,
            })
            .then((permissionsResponse) => {
                if (permissionsResponse.success) {
                    let allPermissions = [...(permissionsResponse.data?.globalPermissions || [])];
                    if (permissionsResponse.data?.retailerPermissions?.length) {
                        permissionsResponse.data.retailerPermissions.forEach((item: any) => {
                            if (item.permissions?.length) {
                                allPermissions = [...allPermissions, ...item.permissions];
                            }
                        });
                    }
                    setPermissions(allPermissions);
                }
            })
            .catch((e) => {
                alert('Error: ' + e + '. Please refresh & try again.');
                logger.error('Failed to fetch account permissions', { error: e });
            });
    }, [abortSignalRef, account]);

    const showContentSetting = checkPermission('DASHBOARD002');

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);

    const clearSelection = async () => {
        setAllCases([]);
        signalAbort();
        setSelectedRetailer([]);
        await fetchSearchHistory();
    };

    const fetchAllRetailers = async (query: string) => {
        try {
            if (!query.replace(/\s/g, '').length) {
                await clearSelection();
                return;
            }

            if (isNonRetailer) {
                const response = await qmrsService.fetchRetailers({
                    searchParams: { query, canCreateQmr: true },
                    signal: abortSignalRef.current,
                    ignoreCache: true,
                });

                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }
                if (response.data.retailers.length) {
                    setAllRetailers(
                        response.data.retailers.map((d) => ({
                            retailerName: d.name,
                            retailerNumber: d.code,
                        }))
                    );
                }
            }
        } catch (e) {
            alert('Error: ' + e + '. Please refresh & try again.');
            logger.error('Failed to search retailers', { error: e });
        }
    };

    const handleSetRetailer = (selected: RetailerSearchResult[]) => {
        if (selected.length) {
            setAllCases([]);
        }
        setSelectedRetailer(selected);
    };

    const searchCases = useCallback(async () => {
        if (loadingCaseList) {
            return;
        }
        try {
            if (selectedRetailer?.[0]?.retailerNumber) {
                setLoadingCaseList(true);

                const searchText = vinTechlineCaseDashboardRouteMatch
                    ? selectedVin?.vinRecord.vin
                        ? selectedVin?.vinRecord.vin
                        : ''
                    : searchQuery;

                const is17CharVin = /^[A-HJ-NPR-Z0-9]{17}$/i.test(searchText);
                const is8CharVin = /^[A-HJ-NPR-Z0-9]{8}$/i.test(searchText);
                const isTechlineCaseNumber = /^\d{6}-\d{7}$/.test(searchText);

                let techlineCaseStatus;
                if (currentStatusFilter === 'ALL') {
                    techlineCaseStatus = '';
                } else {
                    techlineCaseStatus = currentStatusFilter;
                }
                const filterParams: any = {};

                if (filters.selectedWorksheets?.length) {
                    const worksheetValues = filters.selectedWorksheets.map(
                        (worksheet: any) => worksheet.value || worksheet.label
                    );
                    filterParams.worksheets = worksheetValues.join(',');
                }
                if (filters.vin && !is17CharVin && !is8CharVin) {
                    filterParams.vinNumber = filters.vin;
                }

                let mergedTechlineCaseStatus = techlineCaseStatus;
                // Status filter only applies on the All tab; specific tabs exclusively own their status
                if (currentStatusFilter === 'ALL' && filters.selectedStatus?.length) {
                    const filterStatuses = filters.selectedStatus.map((status: any) => status.value);
                    mergedTechlineCaseStatus = [...new Set(filterStatuses)].join(',');
                }

                if (filters.selectedSubmissionDate?.[0]?.label) {
                    filterParams.submissionDate = filters.selectedSubmissionDate[0].label;
                }

                const techlineFilters = {
                    retailerNumber: selectedRetailer[0].retailerNumber,
                    page: pageIndex + 1,
                    recordCount: pageSize,
                    ...(is17CharVin || is8CharVin ? { vinNumber: searchText } : {}),
                    ...(isTechlineCaseNumber ? { caseNumber: searchText } : {}),
                    techlineCaseStatus: mergedTechlineCaseStatus,
                    ...filterParams,
                };

                logger.debug(`Retrieving Techline cases [page ${pageIndex + 1}]`, { page: pageIndex + 1, pageIndex });

                const allCasesRes = await techlineService.retrieveTechlinecases(
                    techlineFilters,
                    true,
                    abortSignalRef.current
                );

                analyticsService.logEvent(ANALYTICS_EVENTS.USER_SEARCHES_TECHLINE_CASES);

                if (allCasesRes?.data?.results?.length) {
                    setAllCases(allCasesRes.data.results);
                    setTotalCases(Number.parseInt(allCasesRes.data.totalRecords) || 0);
                } else {
                    setAllCases([]);
                    setTotalCases(0);
                }
                setLoadingCaseList(false);
            }
        } catch (e) {
            logger.error('Failed to search Techline cases', { error: e });
            setLoadingCaseList(false);
        }
    }, [
        loadingCaseList,
        searchQuery,
        selectedRetailer,
        pageIndex,
        pageSize,
        abortSignalRef,
        vinTechlineCaseDashboardRouteMatch,
        selectedVin,
        currentStatusFilter,
        filters,
    ]);

    useEffect(() => {
        setPageIndex(0);
        searchCases();
    }, [searchQuery]);

    useEffect(() => {
        setPageIndex(0);
        setFilters(emptyFilters);
        searchCases();
    }, [selectedRetailer?.[0]?.retailerNumber]);

    useEffect(() => {
        setPageIndex(0);
        setFilters((prev: any) => ({ ...prev, selectedStatus: [] }));
        // filters effect will call searchCases with the updated tab status
    }, [currentStatusFilter]);

    useEffect(() => {
        setPageIndex(0);
        searchCases();
    }, [filters]);

    useEffect(() => {
        if (selectedRetailer?.[0]?.retailerNumber) {
            searchCases();
        }
    }, [pageIndex, pageSize]);

    const showDraftCases = useMemo(() => {
        if (permissions.length) {
            return Boolean(
                permissions.find(
                    (permission: { permissionId: string }) => permission.permissionId === ALLOW_VIEW_DRAFT_PERMISSION
                )
            );
        }
        return false;
    }, [permissions]);

    const displayCases = useMemo(() => {
        if (!allCases.length) {
            return [];
        }
        let finalCases: any = [...allCases];
        if (!showDraftCases) {
            finalCases = finalCases.filter(
                (caseItem: { techlineCaseStatus: string }) => !statusMap['DRAFT'].includes(caseItem.techlineCaseStatus)
            );
        }
        return finalCases;
    }, [allCases, showDraftCases]);

    const filteredCasesCount = useMemo(() => {
        return totalCases;
    }, [totalCases]);

    const toggleTechlineFilters = (filters?: any) => {
        toggleOverlay('techlineQuickFilters');
    };

    const handlePaginationClick = useCallback((index: number) => {
        logger.debug(`Handling pagination click [index ${index}]`, { index, pageIndex });
        setPageIndex(index);
    }, []);
    const handlePaginationSizeChange = useCallback((size: number) => {
        setPageSize(size);
        setPageIndex(0);
    }, []);
    const filtersCount = useMemo(() => {
        let count = 0;
        if (filters.vin && filters.vin.length) {
            count += 1;
        }
        if (currentStatusFilter === 'ALL' && filters.selectedStatus && filters.selectedStatus.length) {
            count += 1;
        }
        if (filters.selectedWorksheets && filters.selectedWorksheets.length) {
            count += 1;
        }
        if (filters.selectedSubmissionDate && filters.selectedSubmissionDate.length) {
            count += 1;
        }
        return count;
    }, [filters, currentStatusFilter]);

    useEffect(() => {
        loadDashboards();
    }, []);

    return (
        <>
            {showCloseCaseSuccessPopup && showCloseCaseSuccessPopupForCaseNumber && showCloseCaseSuccessPopupFlag && (
                <div>
                    <AlertBar onClose={() => {}} show={true} success>
                        <Typography>
                            Case "{showCloseCaseSuccessPopupForCaseNumber}" has been successfully closed
                        </Typography>
                    </AlertBar>
                </div>
            )}
            {submitSuccessCaseNumber && (
                <div className="mt-2">
                    <AlertBar onClose={() => setSubmitSuccessCaseNumber(null)} show={true} success>
                        <Typography>
                            Case &quot;{submitSuccessCaseNumber}&quot; has been successfully submitted
                        </Typography>
                    </AlertBar>
                </div>
            )}
            <div className={`d-flex justify-content-between aligh-items-center ${classes.header}`}>
                <Typography color="black" style={styles.headerTitle} variant="h2">
                    Techline cases
                </Typography>
                <div className="d-flex align-items-center flex-row gap-4">
                    <SearchRetailer
                        allRetailers={allRetailers}
                        clearSelection={clearSelection}
                        containerClassName={classes.headerSearchRetailer}
                        fetchRetailers={fetchAllRetailers}
                        placeholder="Select Retailer"
                        recentSearch={fetchSearchHistory}
                        selected={selectedRetailer}
                        setSelected={handleSetRetailer}
                    />
                    {!vinTechlineCaseDashboardRouteMatch && showContentSetting && (
                        <Button onPress={handleEditContent} variant="primary">
                            Edit Content
                        </Button>
                    )}
                </div>
            </div>
            {!vinTechlineCaseDashboardRouteMatch && (
                <div className={`container-fluid ${classes.container}`}>
                    <div className="row gap-md-0 gap-4">
                        {videoCardData.techlineDashboardId && (
                            <div className="col-12 col-md-6">
                                <DataCard
                                    buttonLink={videoCardData.link}
                                    buttonText="View How To's"
                                    description={videoCardData.description}
                                    title="VIDEOS"
                                />
                            </div>
                        )}
                        {quickTipCardData.techlineDashboardId && (
                            <div className="col-12 col-md-6">
                                <DataCard
                                    buttonLink={quickTipCardData.link}
                                    buttonText="View TIPS locator"
                                    description={quickTipCardData.description}
                                    title="QUICK TIPS"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
            <StatusTabs
                currentStatusFilter={currentStatusFilter}
                setCurrentStatusFilter={(value: any) => {
                    setDisplayQueryError(false);
                    /*setSearchQuery('');
                    if (searchInputRef.current) {
                        (searchInputRef.current as any).clear?.();
                    }*/
                    setCurrentStatusFilter(value);
                }}
                setPageIndex={setPageIndex}
                showDraftCases={showDraftCases}
            />
            {!vinTechlineCaseDashboardRouteMatch && (
                <div className={`d-flex justify-content-between align-items-centers ${classes.filtersContainer}`}>
                    <div
                        className={`d-flex align-items-center justify-content-start flex-grow-1 flex-row gap-4 ${classes.filtersLeftContainer}`}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <RecentSearchTypeahead
                                classsName={classes.techlineSearch}
                                defaultInputValue={searchQuery}
                                onChange={(query) => {
                                    const trimmedQuery = query.trim();

                                    const is17CharVin = /^[A-HJ-NPR-Z0-9]{17}$/i.test(trimmedQuery);
                                    const is8CharVin = /^[A-HJ-NPR-Z0-9]{8}$/i.test(trimmedQuery);

                                    const isTechlineCaseNumber = /^\d{6}-\d{7}$/.test(trimmedQuery);

                                    if (is17CharVin || is8CharVin || isTechlineCaseNumber || trimmedQuery === '') {
                                        setSearchQuery(query);
                                        setDisplayQueryError(false);
                                    } else {
                                        setSearchQuery('');
                                        setDisplayQueryError(true);
                                    }
                                }}
                                placeholder="Search by VIN (17 or last 8) or Case #"
                                quickSearchQuery={searchQuery}
                                recentSearches={[]}
                                ref={searchInputRef}
                            />
                            {displayQueryError && (
                                <Typography color="redOne">
                                    Enter full VIN, last 8 of VIN, or full Techline Case #
                                </Typography>
                            )}
                        </div>
                        {!vinTechlineCaseDashboardRouteMatch && (
                            <FiltersComponent
                                emptyFilters={emptyFilters}
                                filtersCount={filtersCount}
                                setFilters={setFilters}
                                toggleTechlineFilters={toggleTechlineFilters}
                            />
                        )}
                    </div>
                    {account?.techlineCapabilities?.canCreateDLTechlineCase && (
                        <Button
                            iconRight={
                                <Icon name="plus" raised raisedContainerStyle={{ width: 20, height: 20 }} size={20} />
                            }
                            iconRightStyle={{ padding: 0, margin: 0 }}
                            onPress={() => {
                                setShowCreateCaseModal(true);
                                analyticsService.logEvent(ANALYTICS_EVENTS.USER_CLICKS_CREATE_A_TECHLINE_CASE);
                            }}
                            style={{ padding: 8, width: 156 }}
                            titleStyle={{ paddingHorizontal: 8 }}
                        >
                            Start new case
                        </Button>
                    )}
                </div>
            )}
            <div className={`container-fluid ${classes.container} ${vinTechlineCaseDashboardRouteMatch ? 'mt-4' : ''}`}>
                <div
                    className={`row d-flex flex-column ${displayCases.length ? classes.outerTable : classes.outerTableEmpty}`}
                >
                    <div className="col-12">
                        <TableRenderer
                            emptyStateProps={{ icon: 'plus', description: <EmptyStateDescription /> }}
                            isLoading={loadingCaseList}
                            outerTableClassName={displayCases.length ? classes.outerTable : classes.outerTableEmpty}
                            selectAll={true}
                            tableBodyRowRenderer={(rowData) => <TechlineTableRow rowData={rowData} />}
                            tableHeaderRowRenderer={() => <TechlineTableHeader />}
                            tableRowsData={displayCases}
                            tableRowsDataSetter={setAllCases}
                        />
                    </div>

                    {!loadingCaseList && filteredCasesCount > 0 && (
                        <div className="col-12 mt-auto">
                            <TablePagination
                                onClick={handlePaginationClick}
                                onSizeChange={handlePaginationSizeChange}
                                page={pageIndex}
                                size={pageSize}
                                total={filteredCasesCount}
                            />
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-between align-items-center mb-5 mt-5 flex-row">
                    <div className="d-flex align-items-center flex-row">
                        <Typography color="blueOne" variant="h6">
                            Still need help with your diagnostics?
                        </Typography>
                        <div className="ml-2 mr-2">
                            <Icon name="phone" size={22} />
                        </div>
                        <Typography color="black" variant="h6">
                            1-866-782-2782
                        </Typography>
                    </div>
                </div>
                {showCreateCaseModal && (
                    <CreateCaseModalV2
                        onHide={(identifier: string | undefined) => {
                            setShowCreateCaseModal(false);
                            if (identifier) {
                                navigate('/techline/' + identifier);
                            }
                        }}
                        show={showCreateCaseModal}
                    />
                )}
                {toggledOverlays.techlineQuickFilters && (
                    <TechlineQuickFilters
                        applyFilters={(newFilter) => {
                            setPageIndex(0);
                            setFilters(newFilter);
                        }}
                        clearFilters={() => setFilters(emptyFilters)}
                        filters={filters}
                        onClose={toggleTechlineFilters}
                        currentStatusFilter={currentStatusFilter}
                    />
                )}
            </div>
        </>
    );
}
